import { cardSurfaceClass } from "@/components/threads/shared.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import type { ApiPath } from "@/lib/http.ts";
import { cn } from "@/lib/utils.ts";
import { routineStateQueries, useSaveRoutineState } from "./routineStateApi.ts";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";

/**
 * The scratch notes a routine's actor carries from one run to the next, in its
 * own words — shown so an operator can see why a routine keeps reaching a
 * conclusion, and correct it when the notes are what is stuck.
 *
 * Internal-only. The user-facing shell gates this behind `internal-features`;
 * the endpoints check admin/employee themselves, since a Statsig gate decides
 * what renders, not what is allowed.
 *
 * `statePath` is the finished endpoint rather than an id, because the same file
 * is reachable through two routers — the app's `/api/a/...` and the admin
 * service's `/api/s/...`. Taking the whole path keeps this component ignorant of
 * which surface it is on, keeps the query key distinct between them, and lets
 * each caller write its `/api/` mount as a literal so `ApiPath` still checks it.
 *
 * Callers must pass `key={basePath}` so switching routines remounts this and
 * drops any in-progress draft. An effect resetting on `basePath` would be the
 * obvious alternative, but web's `check-fix` runs biome with `--unsafe`, which
 * deletes a dependency the effect body does not read — leaving one routine's
 * edits sitting in another routine's editor.
 */
export const RoutineStatePanel = ({ statePath }: { statePath: ApiPath }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const stateQuery = useQuery({
    ...routineStateQueries.detail(statePath),
    // Only fetched once opened: this is a debugging surface, and every routine
    // row would otherwise pay for a file read nobody asked to see.
    enabled: open,
  });

  const saveMutation = useSaveRoutineState(statePath);

  const loaded = stateQuery.data?.content;

  const value = draft ?? loaded ?? "";
  const dirty = draft !== null && draft !== loaded;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn(cardSurfaceClass, "overflow-hidden p-0")}
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-auto w-full items-center justify-start gap-2 rounded-none px-3 py-2 text-sm font-medium"
          data-statsig-name="routine-state-toggle"
        >
          <ChevronRight
            className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`}
            aria-hidden
          />
          What the assistant remembers
          <span className="ml-auto text-xs font-normal text-muted-foreground">internal</span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 border-t border-border px-3 py-3">
        <p className="text-xs text-muted-foreground">
          Scratch notes this routine carries between runs. It rewrites this file at the end of every
          run, so an edit here lasts until the next one. If the routine still reaches the same
          conclusion from its sources, what you remove will come back.
        </p>

        {stateQuery.isPending && <Skeleton className="h-40 w-full" />}

        {stateQuery.isError && (
          <div className="text-sm text-destructive">Could not load the state file.</div>
        )}

        {stateQuery.data && (
          <>
            <div className="font-mono text-xs text-muted-foreground">
              {stateQuery.data.path}
              {!stateQuery.data.exists && " (not written yet)"}
            </div>
            <Textarea
              value={value}
              onChange={(e) => setDraft(e.target.value)}
              rows={16}
              spellCheck={false}
              className="font-mono text-xs"
              aria-label="Routine state file"
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={() =>
                  saveMutation.mutate(value, {
                    onSuccess: () => setDraft(null),
                    onError: () => errorToast("Could not save the state file."),
                  })
                }
                disabled={!dirty || saveMutation.isPending}
                data-statsig-name="routine-state-save"
              >
                {saveMutation.isPending ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setDraft(null)}
                disabled={!dirty || saveMutation.isPending}
                data-statsig-name="routine-state-revert"
              >
                Revert
              </Button>
              {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
            </div>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
